import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { formatDate } from '../../lib/formatUtils';
import { PersonAvatar } from '../../components/PersonAvatar';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { User, Mail, Phone, Briefcase, CalendarDays, Clock, IndianRupee, Loader2 } from 'lucide-react';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

export const MyProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/my/profile').then(r => setProfile(r.data.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  if (!profile) return <div className="text-center py-16 text-muted-foreground">Unable to load profile</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-profile-page">
      <div><h1 className="text-2xl font-bold">My Profile</h1><p className="text-muted-foreground">Your employee details</p></div>

      {/* Profile Header */}
      <Card data-testid="profile-header">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            <PersonAvatar firstName={profile.first_name} lastName={profile.last_name} picture={profile.profile_picture} size="xl" />
            <div>
              <h2 className="text-2xl font-bold">{profile.first_name} {profile.last_name}</h2>
              <p className="text-muted-foreground">{profile.designation}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="bg-primary/10 text-primary">{profile.department}</Badge>
                <Badge className={profile.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}>{profile.status}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Details */}
        <Card data-testid="profile-personal">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Contact Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Email</p><p className="font-medium" data-testid="profile-email">{profile.email}</p></div></div>
              <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Phone</p><p className="font-medium" data-testid="profile-phone">{profile.phone || 'Not set'}</p></div></div>
              <div className="flex items-center gap-3"><User className="h-4 w-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Employee ID</p><p className="font-medium" data-testid="profile-emp-id">{profile.employee_code || profile.employee_id || 'N/A'}</p></div></div>
            </div>
          </CardContent>
        </Card>

        {/* Job Details */}
        <Card data-testid="profile-job">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Job Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3"><Briefcase className="h-4 w-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Designation</p><p className="font-medium">{profile.designation}</p></div></div>
              <div className="flex items-center gap-3"><CalendarDays className="h-4 w-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Joining Date</p><p className="font-medium">{formatDate(profile.joining_date) || 'N/A'}</p></div></div>
              <div className="flex items-center gap-3">
                <IndianRupee className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Salary</p>
                  {(() => {
                    const annual = parseFloat(profile.salary) || 0;
                    if (!annual) return <p className="font-medium">N/A</p>;
                    const monthly = annual / 12;
                    return (
                      <p className="font-medium" data-testid="profile-salary">
                        {formatCurrency(monthly)}/mo
                        <span className="text-xs font-normal text-muted-foreground/70 ml-1.5">({formatCurrency(annual)}/year)</span>
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Work Schedule */}
        <Card className="md:col-span-2" data-testid="profile-schedule">
          <CardContent className="p-6 space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Work Schedule</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Working Days</p>
                <div className="flex gap-1.5">
                  {DAY_LABELS.map((d, i) => (
                    <span key={i} className={`w-9 h-9 rounded-lg text-xs font-medium flex items-center justify-center ${
                      (profile.work_days || [0,1,2,3,4]).includes(i) ? 'bg-primary/20 text-primary' : 'bg-muted/30 text-muted-foreground/40'
                    }`} data-testid={`profile-work-day-${i}`}>{d}</span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div><p className="text-xs text-muted-foreground">Standard Hours/Day</p><p className="text-lg font-bold" data-testid="profile-std-hours">{profile.standard_hours || 8}h</p></div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Working Hours</p>
                  {(() => {
                    const hours = parseFloat(profile.standard_hours) || 8;
                    const days = (profile.work_days || [0,1,2,3,4]).length || 5;
                    const weekly = hours * days;
                    return (
                      <p className="text-lg font-bold" data-testid="profile-working-hours">
                        {weekly}h<span className="text-xs font-normal text-muted-foreground ml-1">/week</span>
                        <span className="block text-xs font-normal text-muted-foreground/70 mt-0.5">{days} days × {hours}h/day</span>
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
